import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  type ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { storageAccountId } from '@frontendlib/common/utils/storage-account';
import Mark from 'mark.js';
import { map } from 'rxjs';
import { AlertComponent } from '@frontendlib/components/components/alert/alert.component';
import { KnowledgeMarkerSelectionTooltipDirective } from '@modules/common/directives/knowledge-marker-selection-tooltip.directive';
import { type Stitch } from '@modules/common/types/stitch';
import { PointerGestureService } from '@modules/common/services/pointer-gesture.service';
import { DragNDropService } from '@modules/drag-n-drop/services/drag-n-drop.service';
import { buildEmailDocument, type EmailDocument, FRAME_SCROLL_ATTRIBUTE, KNOT_MARK_CLASS } from '../../utils/email-document';
import { setEmailFrameDocuments } from '../../utils/email-frame-documents';
import { EmailFrameLoader } from './email-frame-loader.service';
import {
  forwardedKeyboardEventInit,
  forwardedMouseEventInit,
  frameNeedsInnerScroll,
  inlineImageTenant,
  isBuiltEmailDocument,
  measureFrameContent,
  nextFrameHeight,
  observeLazyImages,
  prefetchInlineImages,
  revokeObjectUrls,
} from './mail-body-frame';

/** A runaway layout (a `100vh` block chasing its own frame) stops growing after this many writes. */
const MAX_HEIGHT_WRITES = 500;
/** How long after a consent render the reading position is held while the frame settles. */
const SCROLL_RESTORE_WINDOW_MS = 600;
/** An inline image that takes longer than this shows the placeholder. */
const INLINE_IMAGE_TIMEOUT_MS = 10_000;
/** The app's document listeners (outside-click, list focus, selection) see these on the iframe. */
const FORWARDED_MOUSE_EVENTS = ['mousedown', 'mouseup', 'click'] as const;
/** Escape closing a modal or pop-up, list shortcuts. */
const FORWARDED_KEYBOARD_EVENTS = ['keydown', 'keyup'] as const;
/** Focus moving into the frame this soon after the frame forwarded its own press is that press, not another. */
const PRESS_BEFORE_FOCUS_MS = 1000;

/** Everything one shown document owns, torn down before the next one and on destroy. */
interface FrameView {
  readonly allowRemoteImages: boolean;
  /** The document's own URL and its inline images', revoked with the view. */
  readonly blobUrls: string[];
  readonly lazyImageSources: ReadonlyArray<string>;
  readonly disposers: Array<() => void>;
}

/**
 * Received mail, rendered as the Proton / Fastmail model: DOMPurify builds a complete document whose
 * first `<head>` element is a CSP, written into an `<iframe sandbox="allow-same-origin allow-popups
 * allow-popups-to-escape-sandbox allow-downloads">` (no scripts or forms, never navigating the app). The browser
 * opens every link itself, in a new tab with no opener or referrer, and downloads only from a clicked link. Remote images wait for "Load images",
 * which rebuilds the document; the mail's inline attachments are fetched by the app into `blob:` URLs.
 *
 * WebKit runs no listener on the frame's document, not even the app's own (it has no `allow-scripts`),
 * so the forwarding below works in Chromium and Firefox only; where a parent-side signal exists
 * (focus moving into the frame, the frame's selection read while focused) it covers WebKit too.
 */
@Component({
  selector: 'stch-mail-body',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AlertComponent, KnowledgeMarkerSelectionTooltipDirective],
  templateUrl: './mail-body.component.html',
  // A held stitch is carried by mouse moves the frame would otherwise swallow.
  styles: [':host-context(.held-stitch-dragging) iframe { pointer-events: none; }'],
})
export class MailBodyComponent {
  public readonly body = input.required<string>();
  public readonly message = input.required<Stitch>();
  public readonly highlights = input<ReadonlyArray<string>>([]);
  public readonly showBlockingExternalImagesBanner = input<boolean>(true);
  public readonly onImagesLoaded = output<boolean>();

  /** The current document loads, or tried to load, something remote that waits for consent. */
  protected readonly remoteContent = signal<boolean>(false);
  private readonly dragging = toSignal(inject(DragNDropService).getDraggingDataChanges().pipe(map(Boolean)), {
    initialValue: false,
  });
  private readonly pointerGesture = inject(PointerGestureService).active;
  /** A drag or a held divider passes over the frame instead of into it, so the app keeps its mouse moves. */
  protected readonly frameInert = computed(() => this.dragging() || this.pointerGesture());

  /** Consent belongs to one message and body; another one starts blocked again. */
  private readonly consent = signal<{ readonly body: string; readonly message: Stitch } | null>(null);
  protected readonly imagesLoaded = computed<boolean>(() => {
    const consent = this.consent();
    return !!consent && consent.body === this.body() && consent.message === this.message();
  });
  protected readonly showImageAlert = computed<boolean>(
    () => this.remoteContent() && !this.imagesLoaded() && this.showBlockingExternalImagesBanner(),
  );

  private readonly ngZone = inject(NgZone);
  private readonly frameLoader = inject(EmailFrameLoader);
  private readonly frame = viewChild<ElementRef<HTMLIFrameElement>>('frame');
  private readonly bodyScroll = viewChild<ElementRef<HTMLDivElement>>('bodyScroll');
  private readonly selectionTooltip = viewChild(KnowledgeMarkerSelectionTooltipDirective);
  /** The loaded document of the current view: mark.js paints knots into it. */
  private readonly frameDocument = signal<Document | null>(null);

  /** Bumped by every render; an async step from an older render sees a different number and stops. */
  private generation = 0;
  private prefetch: AbortController | null = null;
  private view: FrameView | null = null;
  private pendingScroll: { readonly top: number; readonly left: number } | undefined;

  constructor() {
    effect(() => {
      const frame = this.frame()?.nativeElement;
      const body = this.body() ?? '';
      // A different message with the same body renders again, blocked.
      this.message();
      const allowRemoteImages = this.imagesLoaded();
      // A fallback to srcdoc renders the current message again.
      this.frameLoader.fallbacks();
      if (frame) {
        untracked(() => this.render(frame, body, allowRemoteImages));
      }
    });

    effect(() => {
      const frameDocument = this.frameDocument();
      const terms = this.highlights();
      if (frameDocument) {
        untracked(() => this.paintHighlights(frameDocument, terms));
      }
    });

    inject(DestroyRef).onDestroy(() => {
      this.generation++;
      this.prefetch?.abort();
      this.endView();
    });
  }

  protected onLoadImages(): void {
    const scroll = this.bodyScroll()?.nativeElement;
    this.pendingScroll = scroll ? { top: scroll.scrollTop, left: scroll.scrollLeft } : undefined;
    // A rebuild from the source message: nothing in the shown DOM is ever promoted to a URL.
    this.consent.set({ body: this.body(), message: this.message() });
    this.onImagesLoaded.emit(true);
  }

  private render(frame: HTMLIFrameElement, body: string, allowRemoteImages: boolean): void {
    const generation = ++this.generation;
    this.prefetch?.abort();
    this.prefetch = null;
    if (!allowRemoteImages) {
      this.pendingScroll = undefined;
    }

    const showBuilt = (
      shown: EmailDocument,
      inlineImageUrls?: ReadonlyMap<string, string>,
      unresolvedInlineImages?: ReadonlySet<string>,
    ): void => {
      // Printing waits for every image, so its document assigns them all up front.
      const print = allowRemoteImages
        ? buildEmailDocument(body, { allowRemoteImages, inlineImageUrls, unresolvedInlineImages }).html
        : shown.html;
      this.show(frame, shown, print, allowRemoteImages, inlineImageUrls ? Array.from(inlineImageUrls.values()) : []);
    };

    const first = buildEmailDocument(body, { allowRemoteImages, lazyRemoteImages: true });
    const sources = first.inlineImageSources;
    // A new message shows at once, inline images as placeholders until fetched. A consent render
    // keeps the same message on screen until its images are ready, so nothing flashes.
    if (sources.length === 0 || !allowRemoteImages) {
      showBuilt(first);
    }
    if (sources.length === 0) {
      return;
    }

    const prefetch = new AbortController();
    this.prefetch = prefetch;
    const timeout = setTimeout(() => prefetch.abort(), INLINE_IMAGE_TIMEOUT_MS);
    const tenantId = inlineImageTenant(this.message().accountId, storageAccountId());
    void prefetchInlineImages(sources, prefetch.signal, tenantId).then((inlineImageUrls) => {
      clearTimeout(timeout);
      if (generation !== this.generation) {
        revokeObjectUrls(inlineImageUrls.values());
        return;
      }
      this.prefetch = null;
      // What could not be fetched (failed, past the cap, timed out) waits for consent like any remote image.
      const unresolvedInlineImages = new Set(sources.filter((source) => !inlineImageUrls.has(source)));
      showBuilt(
        buildEmailDocument(body, { allowRemoteImages, inlineImageUrls, unresolvedInlineImages, lazyRemoteImages: true }),
        inlineImageUrls,
        unresolvedInlineImages,
      );
    });
  }

  private show(
    frame: HTMLIFrameElement,
    emailDocument: EmailDocument,
    printHtml: string,
    allowRemoteImages: boolean,
    inlineBlobUrls: ReadonlyArray<string>,
  ): void {
    this.endView();
    const view: FrameView = {
      allowRemoteImages,
      blobUrls: [...inlineBlobUrls],
      lazyImageSources: emailDocument.lazyImageSources,
      disposers: [],
    };
    this.view = view;
    this.remoteContent.set(emailDocument.remoteContent);
    setEmailFrameDocuments(frame, { shown: emailDocument.html, print: printHtml });

    const onLoad = (): void => {
      const frameDocument = frame.contentDocument;
      if (this.view !== view || !frameDocument || !isBuiltEmailDocument(frameDocument)) {
        return;
      }
      frame.removeEventListener('load', onLoad);
      this.ngZone.run(() => this.attach(frame, frameDocument, view));
    };
    frame.addEventListener('load', onLoad);
    view.disposers.push(() => frame.removeEventListener('load', onLoad));
    const documentUrl = this.frameLoader.load(frame, emailDocument.html);
    if (documentUrl) {
      view.blobUrls.push(documentUrl);
    }
  }

  /** Wires one loaded document; every listener, observer and timer lands in the view's disposers. */
  private attach(frame: HTMLIFrameElement, frameDocument: Document, view: FrameView): void {
    const listen = <K extends keyof DocumentEventMap>(
      type: K,
      listener: (event: DocumentEventMap[K]) => void,
      capture = false,
    ): void => {
      frameDocument.addEventListener(type, listener, capture);
      view.disposers.push(() => frameDocument.removeEventListener(type, listener, capture));
    };

    // A backstop only: violations raised while the document is parsed fire before this listener exists,
    // so the builder's scan (escapes resolved, CSS through the browser's parser) carries the offer.
    listen('securitypolicyviolation', (event) => {
      if (!view.allowRemoteImages && event.effectiveDirective.startsWith('img-src') && this.view === view) {
        this.ngZone.run(() => this.remoteContent.set(true));
      }
    });

    // Links are the browser's: the builder gives each one target=_blank and rel="noopener noreferrer", and
    // the sandbox lets that open a normal tab (plain click, middle-click, Cmd/Ctrl-click) in every engine.
    let lastForwardedPress = Number.NEGATIVE_INFINITY;
    for (const type of FORWARDED_MOUSE_EVENTS) {
      listen(type, (event) => {
        if (type === 'mousedown') {
          lastForwardedPress = performance.now();
        }
        frame.dispatchEvent(new MouseEvent(type, forwardedMouseEventInit(event, frame)));
      });
    }
    this.reportFocusAsPress(frame, view, () => lastForwardedPress);
    for (const type of FORWARDED_KEYBOARD_EVENTS) {
      listen(type, (event) => frame.dispatchEvent(new KeyboardEvent(type, forwardedKeyboardEventInit(event))));
    }

    view.disposers.push(observeLazyImages(frameDocument, view.lazyImageSources));

    const releaseSelection = this.selectionTooltip()?.observeFrame(frame);
    if (releaseSelection) {
      view.disposers.push(releaseSelection);
    }

    this.trackHeight(frame, frameDocument, view);
    this.frameDocument.set(frameDocument);
    view.disposers.push(() => {
      if (this.frameDocument() === frameDocument) {
        this.frameDocument.set(null);
      }
    });
  }

  /**
   * The app window blurs when focus moves into the frame, and the iframe becomes the active element: a
   * click in the mail. Where the frame did not just forward that press itself (WebKit forwards nothing),
   * the same press is dispatched on the iframe, so outside-click closes pop-ups as it does elsewhere.
   */
  private reportFocusAsPress(frame: HTMLIFrameElement, view: FrameView, lastForwardedPress: () => number): void {
    let check: ReturnType<typeof setTimeout> | undefined;
    const onBlur = (): void => {
      clearTimeout(check);
      // The active element settles after the window's blur.
      check = setTimeout(() => {
        if (
          this.view !== view ||
          document.activeElement !== frame ||
          performance.now() - lastForwardedPress() < PRESS_BEFORE_FOCUS_MS
        ) {
          return;
        }
        for (const type of FORWARDED_MOUSE_EVENTS) {
          frame.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, composed: true }));
        }
      });
    };
    this.ngZone.runOutsideAngular(() => window.addEventListener('blur', onBlur));
    view.disposers.push(() => {
      window.removeEventListener('blur', onBlur);
      clearTimeout(check);
    });
  }

  private trackHeight(frame: HTMLIFrameElement, frameDocument: Document, view: FrameView): void {
    let writes = 0;
    const measure = (): void => {
      if (this.view !== view) {
        return;
      }
      const content = measureFrameContent(frameDocument);
      // Past the cap (or once the runaway backstop stops resizing) the frame scrolls itself, so nothing
      // is unreachable; otherwise its document never shows a scrollbar beside the reader's.
      const scrolls = frameNeedsInnerScroll(content) || writes >= MAX_HEIGHT_WRITES;
      const root = frameDocument.documentElement;
      if (root.hasAttribute(FRAME_SCROLL_ATTRIBUTE) !== scrolls) {
        root.toggleAttribute(FRAME_SCROLL_ATTRIBUTE, scrolls);
      }
      if (writes >= MAX_HEIGHT_WRITES) {
        return;
      }
      const height = nextFrameHeight(content, frame.offsetHeight);
      if (height !== null) {
        writes++;
        // Written directly, not through change detection, so the reading position below can be
        // restored against the new height in the same task.
        frame.style.height = `${height}px`;
      }
      this.restoreScroll();
    };

    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(frameDocument.documentElement);
      view.disposers.push(() => observer.disconnect());
    }
    if (this.pendingScroll) {
      const settle = setTimeout(() => (this.pendingScroll = undefined), SCROLL_RESTORE_WINDOW_MS);
      view.disposers.push(() => clearTimeout(settle));
    }
  }

  private restoreScroll(): void {
    const scroll = this.bodyScroll()?.nativeElement;
    if (scroll && this.pendingScroll) {
      scroll.scrollTop = this.pendingScroll.top;
      scroll.scrollLeft = this.pendingScroll.left;
    }
  }

  private paintHighlights(frameDocument: Document, terms: ReadonlyArray<string>): void {
    if (!frameDocument.body) {
      return;
    }
    try {
      const marker = new Mark(frameDocument.body);
      marker.unmark({
        element: 'span',
        className: KNOT_MARK_CLASS,
        done: () => {
          if (terms.length > 0) {
            marker.mark([...terms], {
              acrossElements: true,
              element: 'span',
              className: KNOT_MARK_CLASS,
              separateWordSearch: false,
            });
          }
        },
      });
    } catch (error) {
      console.error('Failed to apply highlights:', error);
    }
  }

  private endView(): void {
    const view = this.view;
    if (!view) {
      return;
    }
    this.view = null;
    view.disposers.splice(0).forEach((dispose) => dispose());
    revokeObjectUrls(view.blobUrls);
  }
}
